import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { 
  X, Sparkles, Zap, TrendingUp, Lock, Gift,
  ArrowRight, CheckCircle2, Clock, Star
} from 'lucide-react';

const USAGE_STORAGE_KEY = 'clarias_feature_usage';
const PROMPT_COOLDOWN_KEY = 'clarias_prompt_cooldown';

/**
 * SoftUpgradePrompt - Contextual upgrade prompts after feature use
 */
export const SoftUpgradePrompt = ({ 
  feature = 'answer',
  usageCount = 0,
  maxFree = 5,
  onDismiss 
}) => {
  const navigate = useNavigate();
  const [isVisible, setIsVisible] = useState(false);

  const promptVariants = {
    answer: {
      title: "You're on a roll! 🔥",
      subtitle: `${usageCount} of ${maxFree} free answers used today`,
      benefit: "PRO users get unlimited generations with verified 2025 data",
      icon: Zap,
      color: "amber"
    },
    evaluation: {
      title: "Great progress! 📈",
      subtitle: "You've evaluated your answer",
      benefit: "PRO unlocks detailed examiner-style feedback with improvement plans",
      icon: TrendingUp,
      color: "emerald"
    },
    pyq: {
      title: "Smart analysis! 🎯",
      subtitle: "You explored PYQ trends",
      benefit: "PRO reveals deep patterns and predicted questions for 2025",
      icon: Star,
      color: "purple"
    },
    limit: {
      title: "Daily limit reached",
      subtitle: `You've used all ${maxFree} free answers today`,
      benefit: "Upgrade to PRO for unlimited access and premium features",
      icon: Lock,
      color: "red"
    }
  };

  const variant = promptVariants[feature] || promptVariants.answer;
  const Icon = variant.icon;

  useEffect(() => {
    // Check cooldown to avoid showing too frequently
    const lastPrompt = localStorage.getItem(PROMPT_COOLDOWN_KEY);
    const cooldownPeriod = 5 * 60 * 1000; // 5 minutes
    
    if (lastPrompt && Date.now() - parseInt(lastPrompt) < cooldownPeriod) {
      return;
    }

    // Show prompt after a short delay
    const timer = setTimeout(() => {
      setIsVisible(true);
      localStorage.setItem(PROMPT_COOLDOWN_KEY, Date.now().toString());
    }, 1000);

    return () => clearTimeout(timer);
  }, []);

  const handleDismiss = () => {
    setIsVisible(false);
    onDismiss?.();
  };

  const handleUpgrade = () => {
    navigate('/login');
  };

  if (!isVisible) return null;

  return (
    <div className="fixed bottom-16 sm:bottom-4 left-2 right-2 sm:left-auto sm:right-4 z-50 sm:max-w-sm animate-in slide-in-from-bottom-5 duration-300">
      <Card className={`border-2 border-${variant.color}-200 shadow-2xl bg-white overflow-hidden`}>
        {/* Colored Top Bar */}
        <div className={`h-1 bg-gradient-to-r from-${variant.color}-400 to-${variant.color}-600`} />
        
        <CardContent className="p-3 sm:p-4">
          {/* Close Button */}
          <button
            onClick={handleDismiss}
            className="absolute top-2 right-2 text-slate-400 hover:text-slate-600"
          >
            <X className="w-4 h-4" />
          </button>

          {/* Content */}
          <div className="flex gap-3">
            <div className={`w-10 h-10 rounded-full bg-${variant.color}-100 flex items-center justify-center flex-shrink-0`}>
              <Icon className={`w-5 h-5 text-${variant.color}-600`} />
            </div>
            <div className="flex-1">
              <h4 className="font-bold text-slate-900 text-sm">{variant.title}</h4>
              <p className="text-xs text-slate-500 mt-0.5">{variant.subtitle}</p>
              <p className={`text-xs text-${variant.color}-600 mt-2 font-medium`}>
                ✨ {variant.benefit}
              </p>
            </div>
          </div>

          {/* Usage Progress Bar */}
          {feature !== 'limit' && (
            <div className="mt-3 mb-3">
              <div className="flex justify-between text-xs text-slate-500 mb-1">
                <span>Daily Usage</span>
                <span>{usageCount}/{maxFree}</span>
              </div>
              <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                <div 
                  className={`h-full bg-gradient-to-r from-${variant.color}-400 to-${variant.color}-600 transition-all duration-500`}
                  style={{ width: `${Math.min((usageCount / maxFree) * 100, 100)}%` }}
                />
              </div>
            </div>
          )}

          {/* CTA Buttons */}
          <div className="flex gap-2 mt-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleDismiss}
              className="text-slate-500 text-xs"
            >
              Maybe Later
            </Button>
            <Button
              size="sm"
              onClick={handleUpgrade}
              className={`flex-1 bg-${variant.color}-500 hover:bg-${variant.color}-600 text-white text-xs`}
              data-testid="soft-upgrade-btn"
            >
              <Sparkles className="w-3 h-3 mr-1" />
              Upgrade to PRO
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

/**
 * FeatureLimitBanner - Banner shown when approaching or reaching limits
 */
export const FeatureLimitBanner = ({ 
  usageCount = 0, 
  maxFree = 5,
  onUpgrade 
}) => {
  const navigate = useNavigate();
  const remaining = maxFree - usageCount;
  const isLow = remaining <= 2 && remaining > 0;
  const isExhausted = remaining <= 0;

  if (remaining > 2) return null;

  return (
    <div className={`rounded-lg p-3 mb-4 flex items-center justify-between ${
      isExhausted 
        ? 'bg-red-50 border border-red-200' 
        : 'bg-amber-50 border border-amber-200'
    }`}>
      <div className="flex items-center gap-2">
        {isExhausted ? (
          <Lock className="w-4 h-4 text-red-500" />
        ) : (
          <Clock className="w-4 h-4 text-amber-500" />
        )}
        <span className={`text-sm ${isExhausted ? 'text-red-700' : 'text-amber-700'}`}>
          {isExhausted 
            ? "Daily limit reached. Upgrade for unlimited access."
            : `Only ${remaining} free ${remaining === 1 ? 'generation' : 'generations'} left today!`
          }
        </span>
      </div>
      <Button
        size="sm"
        variant="ghost"
        onClick={() => navigate('/login')}
        className={`text-xs ${isExhausted ? 'text-red-600 hover:text-red-700' : 'text-amber-600 hover:text-amber-700'}`}
      >
        <Sparkles className="w-3 h-3 mr-1" />
        Upgrade
      </Button>
    </div>
  );
};

/**
 * ProFeatureTeaser - Shows locked PRO features with upgrade prompt
 */
export const ProFeatureTeaser = ({ 
  featureName = "Premium Feature",
  description = "This feature is available for PRO users",
  benefits = []
}) => {
  const navigate = useNavigate();

  return (
    <Card className="border-2 border-dashed border-indigo-200 bg-gradient-to-br from-indigo-50/50 to-purple-50/50">
      <CardContent className="p-6 text-center">
        <div className="w-12 h-12 rounded-full bg-indigo-100 flex items-center justify-center mx-auto mb-4">
          <Lock className="w-6 h-6 text-indigo-500" />
        </div>
        
        <Badge className="bg-indigo-100 text-indigo-700 mb-2">PRO Feature</Badge>
        <h3 className="font-bold text-slate-900 mb-2">{featureName}</h3>
        <p className="text-sm text-slate-600 mb-4">{description}</p>
        
        {benefits.length > 0 && (
          <div className="space-y-2 mb-4 text-left">
            {benefits.map((benefit, idx) => (
              <div key={idx} className="flex items-center gap-2 text-sm text-slate-600">
                <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                <span>{benefit}</span>
              </div>
            ))}
          </div>
        )}
        
        <Button
          onClick={() => navigate('/login')}
          className="bg-indigo-600 hover:bg-indigo-700 text-white"
          data-testid="pro-teaser-upgrade-btn"
        >
          <Sparkles className="w-4 h-4 mr-2" />
          Unlock with PRO
          <ArrowRight className="w-4 h-4 ml-2" />
        </Button>
      </CardContent>
    </Card>
  );
};

/**
 * useFeatureUsage - Hook to track feature usage
 */
export const useFeatureUsage = () => {
  const [usage, setUsage] = useState({});

  useEffect(() => {
    const stored = localStorage.getItem(USAGE_STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      // Reset if it's a new day
      const today = new Date().toDateString();
      if (parsed.date !== today) {
        const newUsage = { date: today, counts: {} };
        localStorage.setItem(USAGE_STORAGE_KEY, JSON.stringify(newUsage));
        setUsage(newUsage);
      } else {
        setUsage(parsed);
      }
    } else {
      const newUsage = { date: new Date().toDateString(), counts: {} };
      localStorage.setItem(USAGE_STORAGE_KEY, JSON.stringify(newUsage));
      setUsage(newUsage);
    }
  }, []);

  const trackUsage = (feature) => {
    setUsage(prev => {
      const newUsage = {
        ...prev,
        counts: {
          ...prev.counts,
          [feature]: (prev.counts?.[feature] || 0) + 1
        }
      };
      localStorage.setItem(USAGE_STORAGE_KEY, JSON.stringify(newUsage));
      return newUsage;
    });
  };

  const getUsageCount = (feature) => {
    return usage.counts?.[feature] || 0;
  };

  return { trackUsage, getUsageCount, usage };
};

export default SoftUpgradePrompt;
